<template>
    <div
        ref="hoverIndicator"
        class="HoverIndicator"
        :class="[`hover-indicator-${variant}`, { 'is-visible': isVisible }, customClass]"
        :style="indicatorStyle"
    ></div>
</template>

<script>
import { gsap } from 'gsap';

export default {
    name: 'HoverIndicator',
    props: {
        variant: {
            type: String,
            default: 'default',
            validator: (value) => ['default', 'subtle', 'glow', 'border', 'tab'].includes(value),
        },
        animationDuration: {
            type: Number,
            default: 200,
        },
        animationEasing: {
            type: String,
            default: 'easeOutCubic',
        },
        hoverDelay: {
            type: Number,
            default: 500,
        },
        zIndex: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            isVisible: false,
            hoverTimeout: null,
            isHovering: false,
            indicatorStyle: {
                opacity: 0,
                transform: 'translateX(0px) translateY(0px)',
                width: '0px',
                height: '0px',
                zIndex: this.zIndex,
            },
            isInDashboardTable: false,
            isInNavigation: false,
            navigationHoverTarget: null,
            navigationObserver: null,
            isNavigationReady: false,
            navigationReadyRetries: 0,
            maxNavigationReadyRetries: 50,
        };
    },
    methods: {
        handleHover(event) {
            if (this.hoverTimeout) {
                clearTimeout(this.hoverTimeout);
                this.hoverTimeout = null;
            }
            this.isHovering = true;
            this.isVisible = true;
            const target = event.currentTarget;
            const container = this.$parent.$el;
            const hoverIndicator = this.$refs.hoverIndicator;
            if (!hoverIndicator || !target || !container) return;
            const targetRect = target.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const paddingX = this.getPaddingX();
            const paddingY = this.getPaddingY();
            const x = targetRect.left - containerRect.left - paddingX;
            const y = targetRect.top - containerRect.top - paddingY;
            const width = targetRect.width + paddingX * 2;
            const height = targetRect.height + paddingY * 2;
            gsap.to(hoverIndicator, {
                x: x,
                y: y,
                width: width,
                height: height,
                opacity: 1,
                duration: this.animationDuration / 1000,
                ease: this.getGsapEase(),
            });
        },
        handleLeave() {
            this.isHovering = false;

            if (!this.isInNavigation) {
                const delay = this.hoverDelay;
                this.hoverTimeout = setTimeout(() => {
                    if (!this.isHovering) {
                        this.hideIndicator();
                    }
                }, delay);
            }
        },
        handleNavigationLeave() {
            this.hideIndicator();
        },
        handleNavigationMouseMove(event) {
            if (this.isInNavigation && !this.isHovering) {
                this.isHovering = true;
            }
        },
        hideIndicator() {
            const hoverIndicator = this.$refs.hoverIndicator;
            if (!hoverIndicator) return;
            gsap.to(hoverIndicator, {
                opacity: 0,
                duration: this.animationDuration / 1000,
                ease: 'power1.in',
                onComplete: () => {
                    this.isVisible = false;
                },
            });
        },
        getPaddingX() {
            switch (this.variant) {
                case 'subtle':
                    return 8;
                case 'glow':
                    return 12;
                case 'border':
                    return 6;
                case 'tab':
                    return 0;
                default:
                    return 12;
            }
        },
        getPaddingY() {
            switch (this.variant) {
                case 'subtle':
                    return 2;
                case 'glow':
                    return 4;
                case 'border':
                    return 2;
                case 'tab':
                    return 0;
                default:
                    return 4;
            }
        },
        getGsapEase() {
            const easingMap = {
                easeOutCubic: 'power2.out',
                easeInCubic: 'power2.in',
                easeInOutCubic: 'power2.inOut',
                easeOutQuad: 'power1.out',
                easeInQuad: 'power1.in',
                easeInOutQuad: 'power1.inOut',
            };
            return easingMap[this.animationEasing] || 'power2.out';
        },
        showHover(targetElement) {
            if (!targetElement) return;
            const event = {
                currentTarget: targetElement,
            };
            this.handleHover(event);
        },
        showTableRowHover(targetElement) {
            if (!targetElement) return;
            const hoverIndicator = this.$refs.hoverIndicator;
            const container = this.$parent.$el;
            if (!hoverIndicator || !container) return;
            const targetRect = targetElement.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const paddingX = 0;
            const paddingY = this.getPaddingY();
            const x = 0;
            const y = targetElement.offsetTop;
            const width = targetRect.width - 2;
            const height = targetRect.height - 2;
            gsap.to(hoverIndicator, {
                x: x,
                y: y,
                width: width,
                height: height,
                opacity: 1,
                duration: this.animationDuration / 1000,
                ease: this.getGsapEase(),
            });
        },
        showCalendarDayHover(targetElement) {
            if (!targetElement) return;
            const hoverIndicator = this.$refs.hoverIndicator;
            const container = this.$parent.$el;
            if (!hoverIndicator || !container) return;
            const targetRect = targetElement.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const paddingX = 0;
            const paddingY = 0;
            const x = targetElement.offsetLeft - paddingX;
            const y = targetElement.offsetTop - paddingY;
            const width = targetElement.offsetWidth + paddingX * 2;
            const height = targetElement.offsetHeight + paddingY * 2;
            gsap.to(hoverIndicator, {
                x: x,
                y: y,
                width: width,
                height: height,
                opacity: 1,
                duration: this.animationDuration / 1000,
                ease: this.getGsapEase(),
            });
        },
        hide() {
            this.hideIndicator();
        },
        setupHoverListeners() {
            this.$nextTick(() => {
                if (this.isInNavigation) {
                    this.waitForNavigationReady();
                } else {
                    this.initializeHoverListeners();
                }
            });
        },

        waitForNavigationReady() {
            const navigationContainer = this.$parent.$el;
            if (!navigationContainer) return;

            if (this.navigationReadyRetries >= this.maxNavigationReadyRetries) {
                this.isNavigationReady = true;
                this.initializeHoverListeners();
                return;
            }

            this.navigationReadyRetries++;

            const businessNameElement = navigationContainer.querySelector('.BusinessName p');
            const navLinks = navigationContainer.querySelectorAll('.Nav-Link');

            const hasBusinessName = businessNameElement && businessNameElement.textContent.trim();
            const hasNavLinks = navLinks.length > 0;

            const expectedMinLinks = this.$parent.isMobile ? 5 : 8;
            const hasEnoughLinks = navLinks.length >= expectedMinLinks;

            if (hasBusinessName && hasNavLinks && hasEnoughLinks) {
                this.isNavigationReady = true;
                this.initializeHoverListeners();
            } else {
                const timeout = this.isNavigationReady ? 200 : 100;
                setTimeout(() => {
                    this.waitForNavigationReady();
                }, timeout);
            }
        },

        initializeHoverListeners() {
            const hoverTargets = this.$parent.$el.querySelectorAll(this.hoverTargets);
            hoverTargets.forEach((target) => {
                target.addEventListener('mouseenter', this.handleHover);
                target.addEventListener('mouseleave', this.handleLeave);
            });

            if (this.isInNavigation) {
                const navigationContainer = this.$parent.$el;
                if (navigationContainer) {
                    navigationContainer.addEventListener('mouseleave', this.handleNavigationLeave);
                    navigationContainer.addEventListener(
                        'mousemove',
                        this.handleNavigationMouseMove,
                    );
                }
            }
        },

        reinitializeListeners() {
            this.removeHoverListeners();

            this.isNavigationReady = false;
            this.navigationReadyRetries = 0;

            setTimeout(() => {
                this.setupHoverListeners();
            }, 50);
        },

        removeHoverListeners() {
            const hoverTargets = this.$parent.$el.querySelectorAll(this.hoverTargets);
            hoverTargets.forEach((target) => {
                target.removeEventListener('mouseenter', this.handleHover);
                target.removeEventListener('mouseleave', this.handleLeave);
            });

            if (this.isInNavigation && this.$parent && this.$parent.$el) {
                this.$parent.$el.removeEventListener('mouseleave', this.handleNavigationLeave);
                this.$parent.$el.removeEventListener('mousemove', this.handleNavigationMouseMove);
            }
        },
        setupNavigationObserver() {
            if (!this.$parent || !this.$parent.$el) return;

            this.navigationObserver = new MutationObserver((mutations) => {
                let shouldReinitialize = false;

                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList') {
                        const addedNodes = Array.from(mutation.addedNodes);
                        const removedNodes = Array.from(mutation.removedNodes);

                        const hasNavChanges =
                            addedNodes.some(
                                (node) =>
                                    node.nodeType === Node.ELEMENT_NODE &&
                                    (node.classList?.contains('Nav-Link') ||
                                        node.querySelector?.('.Nav-Link')),
                            ) ||
                            removedNodes.some(
                                (node) =>
                                    node.nodeType === Node.ELEMENT_NODE &&
                                    (node.classList?.contains('Nav-Link') ||
                                        node.querySelector?.('.Nav-Link')),
                            );

                        if (hasNavChanges) {
                            shouldReinitialize = true;
                        }
                    }
                });

                if (shouldReinitialize) {
                    this.reinitializeListeners();
                }
            });

            this.navigationObserver.observe(this.$parent.$el, {
                childList: true,
                subtree: true,
            });
        },
    },
    computed: {
        hoverTargets() {
            return '.Nav-Link, .hover-target, [data-hover-target]';
        },
        customClass() {
            return this.isInDashboardTable ? 'dashboard-table-hover-indicator' : '';
        },
    },
    mounted() {
        this.setupHoverListeners();
        let parent = this.$parent;
        while (parent) {
            if (parent.$options && parent.$options.name === 'BaseDashboardTable') {
                this.isInDashboardTable = true;
                break;
            }
            if (parent.$options && parent.$options.name === 'AppNavigation') {
                this.isInNavigation = true;
                break;
            }
            parent = parent.$parent;
        }

        if (this.isInNavigation) {
            this.setupNavigationObserver();
        }
    },

    beforeDestroy() {
        if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
        }

        this.removeHoverListeners();

        if (this.navigationObserver) {
            this.navigationObserver.disconnect();
        }
    },
};
</script>

<style lang="scss" scoped>
.HoverIndicator {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
    border-radius: 6px;
    transition: opacity 0.2s ease;
    will-change: transform, width, height, opacity;

    &.hover-indicator-default {
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(4px);
        border: 0.5px solid rgba(255, 255, 255, 0.2);
    }

    &.hover-indicator-subtle {
        background: rgba(0, 0, 0, 0.05);
        border: 0.5px solid rgba(0, 0, 0, 0.1);
    }

    &.hover-indicator-glow {
        background: rgba(59, 130, 246, 0.1);
        box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
        border: 0.5px solid rgba(59, 130, 246, 0.2);
    }

    &.hover-indicator-border {
        background: transparent;
        border: 0.5px solid rgba(59, 130, 246, 0.6);
        box-shadow: 0 0 10px rgba(59, 130, 246, 0.2);
    }

    &.hover-indicator-tab {
        background: rgba(0, 0, 0, 0.04);
        border: 0.5px solid rgba(0, 0, 0, 0.04);
        border-radius: 4px;
        /* Match tab button radius */
    }

    &.dashboard-table-hover-indicator {
        z-index: 20 !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        border: 0.5px solid rgba(var(--color-blue-rgb), 0.3);
        background: rgba(var(--color-blue-rgb), 0.07);
    }

    @media screen and (max-width: $breakpoint-tablet-portrait) {
        display: none;
    }
}
</style>
